import { useCallback, useSyncExternalStore } from 'react'

/**
 * Viewport-driven media query, kept in sync with the SCSS breakpoints.
 *
 * Used so a placement the stylesheet hides is not even mounted — no DOM, and
 * no request for an image nobody will see.
 */
export default function useMediaQuery(query: string): boolean {
    const subscribe = useCallback((onChange: () => void) => {
        if (typeof window === 'undefined' || !window.matchMedia) return () => { }

        const list = window.matchMedia(query)

        if (list.addEventListener) {
            list.addEventListener('change', onChange)
        } else {
            // Safari < 14 only has the deprecated form
            ;(list as any).addListener(onChange)
        }
        // `change` is the accurate signal, but it is not delivered in every
        // situation a viewport can change size (devtools/CDP overrides, some
        // mobile chrome resizes). `resize` is the safety net; getSnapshot is
        // cheap and useSyncExternalStore skips the render when it is unchanged.
        window.addEventListener('resize', onChange)

        return () => {
            if (list.removeEventListener) {
                list.removeEventListener('change', onChange)
            } else {
                ;(list as any).removeListener(onChange)
            }
            window.removeEventListener('resize', onChange)
        }
    }, [query])

    const getSnapshot = useCallback(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return false
        return window.matchMedia(query).matches
    }, [query])

    return useSyncExternalStore(subscribe, getSnapshot, () => false)
}
